import React, { useCallback, useLayoutEffect, useRef } from 'react';

/*
  THE NAVIGATION PIECES, rebrand step 4, 17 September 2026 (brand guide 11.7,
  12.2 and 10.5). Drawn by the top bar (AppHeader.jsx) and the two frames
  (AppFrame.jsx); no screen draws these itself.

  **What moves and what does not.** Every destination here comes from the one
  menu in src/lib/menu.jsx, already filtered by job level, by the modules
  switched on in Site Settings and by Global Admin, exactly as before. Nothing
  in this file decides who may go where. It only decides where on the screen the
  way in is drawn:

    areas              links in the top bar (600 and up), the tab bar (under 600)
    an area's pages    a row of pills at the top of the page, when it has more
                       than one, which is the rule the old page row had
    Settings           the avatar menu (guide 11.7), for every job level
*/

export const SETTINGS_AREA = 'settings_area';

// The guide's 12.2: five destinations at most in the tab bar.
export const TAB_BAR_MAX = 5;

/* Settings comes out of the areas and goes to the avatar menu. */
/*
  **`keepSettings` puts Settings back among the areas**, the owner's decision of
  3 October 2026: *"I hate that you have so many settings now living in the user
  drop down menu thing."* It is a deliberate departure from the brand guide's
  11.7, which says Settings is in the avatar menu for every role, and the
  departure is recorded in `docs/design/phase-3/DECISIONS.md`.

  **Only the desk bar passes it.** Below 1024 Settings stays in the account
  sheet, because he asked for the dock to be left exactly as it is, and because
  a tab bar of five is already at the guide's limit.

  `settings` comes back either way, so the avatar menu can still list the same
  pages for anyone used to finding them there.
*/
export function splitMenu(areas = [], { keepSettings = false } = {}) {
  return {
    main: keepSettings ? areas : areas.filter(a => a.key !== SETTINGS_AREA),
    settings: areas.find(a => a.key === SETTINGS_AREA) || null,
  };
}

/*
  The tab bar's destinations, and anything past five. No job level has more than
  four once Settings has moved (an administrator has Home, Operations, Ordering
  and Reports), so the overflow is always empty today. It is handled rather than
  assumed: anything past five is listed in the account sheet, so a menu that
  grows cannot lose a way in.
*/
export function tabSplit(areas = []) {
  return { shown: areas.slice(0, TAB_BAR_MAX), overflow: areas.slice(TAB_BAR_MAX) };
}

export function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* Your initials in a MedEx blue circle (the reference's avatar). */
export function Avatar({ name, size = 40 }) {
  return (
    <span className="oh-avatar" aria-hidden="true"
      style={{ width: size, height: size, fontSize: size >= 40 ? 14 : 13 }}>
      {initialsOf(name)}
    </span>
  );
}

const areaHas = (area, key) => !!area && area.pages.some(p => p.key === key);

/* The areas, as links in the top bar. */
export function NavLinks({ areas = [], activeKey, onSelect }) {
  return (
    <nav className="oh-navlinks" aria-label="Main">
      {areas.map(a => {
        const on = areaHas(a, activeKey);
        return (
          <button key={a.key} type="button" className={'oh-navlink' + (on ? ' on' : '')}
            aria-current={on ? 'page' : undefined}
            onClick={() => onSelect && onSelect(a.pages[0].key)}>
            {a.label}
          </button>
        );
      })}
    </nav>
  );
}

/* The areas, as the phone's glass tab bar (guide 12.2). */
export function TabBar({ areas = [], activeKey, onSelect }) {
  const { shown } = tabSplit(areas);
  return (
    <nav className="oh-tabbar" aria-label="Main" style={{ gridTemplateColumns: `repeat(${Math.max(shown.length, 1)}, minmax(0, 1fr))` }}>
      {shown.map(a => {
        const on = areaHas(a, activeKey);
        return (
          <button key={a.key} type="button" className={'oh-tab' + (on ? ' on' : '')}
            aria-current={on ? 'page' : undefined}
            aria-label={a.tabLabel ? a.label : undefined}
            onClick={() => onSelect && onSelect(a.pages[0].key)}>
            <span className="oh-tab-ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {a.pages[0].icon}
              </svg>
            </span>
            {/* An area may carry a shorter name for this bar only, where a tab is
                about 78 pixels wide on a 375 phone. */}
            <span className="oh-tab-label">{a.tabLabel || a.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/*
  An area's pages, as pills at the top of the page (the reference's pill row).
  Drawn only when the area has more than one page, which is the rule the page
  row in the old bar had. "Not built yet" stays on the page it was on.
*/
export function PageTabs({ area, activeKey, onSelect }) {
  if (!area || area.pages.length < 2) return null;
  return (
    <nav className="oh-subnav" aria-label={area.label + ' pages'}>
      {area.pages.map(p => {
        const on = p.key === activeKey;
        return (
          <button key={p.key} type="button" className={'oh-pill' + (on ? ' on' : '')}
            aria-current={on ? 'page' : undefined}
            onClick={() => onSelect && onSelect(p.key)}>
            {p.label}
            {p.soon && <span className="oh-pill-soon">Not built yet</span>}
          </button>
        );
      })}
    </nav>
  );
}

const ICON_SIGN_OUT = (
  <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>
);

/*
  The sheet your initials open below 1024: your Settings pages, then you, then
  Sign out. It is the old More sheet with Settings in it rather than an overflow
  of areas, and like that sheet it has no site switcher (known issue 26).
*/
export function AccountSheet({ open, onClose, settings, overflow = [], activeKey, onSelect, name, email, roleLabel, onSignOut }) {
  if (!open) return null;
  const pick = key => { onClose(); onSelect && onSelect(key); };
  const rows = [
    ...overflow.map(a => ({ key: a.pages[0].key, label: a.label, icon: a.pages[0].icon, on: areaHas(a, activeKey) })),
    ...(settings ? settings.pages.map(p => ({ key: p.key, label: p.label, icon: p.icon, on: p.key === activeKey })) : []),
  ];
  return (
    <>
      <div onClick={onClose} className="oh-sheet-back" />
      <div className="oh-sheet oh-account-sheet" role="dialog" aria-label="Your account and settings">
        <div className="oh-sheet-grab" />
        <div className="more-you">
          <div className="oh-title more-you-name">{name}</div>
          <div className="more-you-email">{email}</div>
          <div className="more-you-role">{roleLabel}</div>
        </div>
        {rows.length > 0 && (
          <div className="more-areas">
            {settings && <div className="more-label">Settings</div>}
            {rows.map(r => (
              <button key={r.key} type="button" className={'more-area' + (r.on ? ' on' : '')}
                aria-current={r.on ? 'page' : undefined} onClick={() => pick(r.key)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{r.icon}</svg>
                <span>{r.label}</span>
              </button>
            ))}
          </div>
        )}
        <button type="button" className="btn btn-outline more-signout" onClick={onSignOut}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON_SIGN_OUT}</svg>
          Sign out
        </button>
      </div>
    </>
  );
}

/*
  THE BELT SLIDE (guide 10.5). The top bar stays put. The page leaving slides
  32 to the left and fades in 200ms on the exit curve; the page arriving slides
  in from 32 to the right in 320ms on the enter curve, 140ms after. Going back
  to an earlier page in the menu's order runs the other way.

  **Nothing about where you end up changes, only when it is drawn.** A way in
  that the frames draw (a link, a tab, a pill, a Settings page) waits the 140ms
  for the old page to leave before switching; any other way a page changes
  (a button on a page, a returning session) slides the new page in without the
  exit. Reduced motion switches at once and moves nothing.

  The page is animated in place, never re-created, so no page loses what it
  was holding because of a slide.
*/
const EXIT = { duration: 200, easing: 'cubic-bezier(.55, 0, .85, .35)' };
const ENTER = { duration: 320, easing: 'cubic-bezier(.16, 1, .3, 1)' };
const HAND_OVER_MS = 140;
const SHIFT = 32;

const reduced = () => typeof window !== 'undefined' && !!window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useBelt(pageKey, order, onSelect) {
  const ref = useRef(null);
  const orderRef = useRef(order);
  orderRef.current = order;
  const last = useRef({ key: pageKey, order: order(pageKey) });
  const leaving = useRef(null);
  const timers = useRef([]);

  const stopLeaving = () => {
    if (leaving.current) { leaving.current.cancel(); leaving.current = null; }
  };

  const go = useCallback(key => {
    if (!onSelect) return;
    const el = ref.current;
    if (key === pageKey || reduced() || !el || !el.animate) { onSelect(key); return; }
    const dir = order(key) < order(pageKey) ? -1 : 1;
    stopLeaving();
    timers.current.forEach(clearTimeout);
    leaving.current = el.animate(
      [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateX(${-SHIFT * dir}px)` }],
      { ...EXIT, fill: 'forwards' },
    );
    timers.current = [
      setTimeout(() => onSelect(key), HAND_OVER_MS),
      // If the switch was refused or went elsewhere, never leave the page hidden.
      setTimeout(stopLeaving, HAND_OVER_MS + 600),
    ];
  }, [onSelect, order, pageKey]);

  useLayoutEffect(() => () => timers.current.forEach(clearTimeout), []);

  useLayoutEffect(() => {
    const prev = last.current;
    if (prev.key === pageKey) return;
    const now = orderRef.current(pageKey);
    last.current = { key: pageKey, order: now };
    stopLeaving();
    const el = ref.current;
    if (!el || !el.animate || reduced()) return;
    const dir = now < prev.order ? -1 : 1;
    el.animate(
      [{ opacity: 0, transform: `translateX(${SHIFT * dir}px)` }, { opacity: 1, transform: 'none' }],
      { ...ENTER, fill: 'backwards' },
    );
  }, [pageKey]);

  return { ref, go };
}
